import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getCreditInfoByStore,
  getCreditPackages,
  getActivePaymentMethods,
  prepareInicisPayment,
} from '../api/credit.api';
import type { CreditPackage, InicisFormData, ActivePaymentMethods } from '../api/credit.api';
import Layout from '../components/layout/Layout';
import styles from './CreditsPage.module.css';

declare global {
  interface Window {
    INIStdPay?: {
      pay: (formId: string) => void;
    };
  }
}

/**
 * KG이니시스 INIStdPay JS SDK 동적 로드.
 * 사전심사 단계: 테스트 가맹점이면 stg 도메인, 운영 가맹점이면 stdpay 도메인.
 */
function loadInicisScript(scriptUrl: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.INIStdPay) {
      resolve();
      return;
    }
    const existing = document.querySelector(
      `script[src="${scriptUrl}"]`,
    ) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('INIStdPay 로드 실패')), {
        once: true,
      });
      return;
    }
    const script = document.createElement('script');
    script.src = scriptUrl;
    script.charset = 'UTF-8';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('INIStdPay 로드 실패'));
    document.head.appendChild(script);
  });
}

const INICIS_FORM_ID = 'inicisStdPayForm';

/**
 * 패키지 가격에 따른 카드사 한도 경고 라벨
 * — KG이니시스 포인트충전 카테고리 카드사별 한도 정책 기준
 */
function getCardLimitWarning(price: number):
  | { label: string; severity: 'warn' | 'danger' }
  | null {
  if (price <= 50_000) return null; // 1K — 모든 카드 OK
  if (price <= 100_000) return { label: '신한 1일 5만원 한도 주의', severity: 'warn' };
  if (price <= 200_000) return { label: '신한·현대·NH 결제 불가', severity: 'warn' };
  if (price <= 500_000) return { label: '대부분 카드사 한도 초과', severity: 'danger' };
  return { label: '비씨/하나카드만 결제 가능', severity: 'danger' };
}

export default function CreditsPage() {
  const { storeId } = useAuth();
  const [balance, setBalance] = useState<number | null>(null);
  const [licenseId, setLicenseId] = useState<string | null>(null);
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [selectedPkg, setSelectedPkg] = useState<CreditPackage | null>(null);
  const [step, setStep] = useState<'list' | 'confirm' | 'complete'>('list');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [inicisForm, setInicisForm] = useState<InicisFormData | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<ActivePaymentMethods>({
    card: true,
    kakaopay: true,
    pendingCards: [],
  });

  const loadData = useCallback(async () => {
    if (!storeId) return;
    try {
      const [info, pkgs, methods] = await Promise.all([
        getCreditInfoByStore(storeId),
        getCreditPackages(),
        getActivePaymentMethods().catch(() => ({ card: true, kakaopay: true, pendingCards: [] as string[] })),
      ]);
      setBalance(info.balance);
      setLicenseId(info.licenseId);
      setPackages(pkgs);
      setPaymentMethods(methods);
    } catch {
      setError('데이터를 불러올 수 없습니다.');
    }
  }, [storeId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSelect = (pkg: CreditPackage) => {
    setSelectedPkg(pkg);
    setStep('confirm');
    setError(null);
    setAgreed(false);
  };

  const handleConfirm = async () => {
    if (!licenseId || !selectedPkg || isProcessing) return;
    if (!agreed) {
      setError('"구매 즉시 서비스 제공 및 청약철회 제한"에 동의해주세요.');
      return;
    }
    setIsProcessing(true);
    setError(null);
    try {
      // 1) 백엔드에서 KG이니시스 서명 포함 폼 데이터 발급
      const form = await prepareInicisPayment(licenseId, selectedPkg.name, {
        name: '만석 이용자',
        tel: '00000000000',
        email: 'noreply@sangsangnode.co.kr',
      });
      // 2) INIStdPay JS SDK 동적 로드
      await loadInicisScript(form.scriptUrl);
      setInicisForm(form);
      // 3) 다음 렌더 사이클에서 form 이 DOM 에 들어간 뒤 결제창 호출
      requestAnimationFrame(() => {
        if (window.INIStdPay) {
          try {
            window.INIStdPay.pay(INICIS_FORM_ID);
          } catch (e) {
            setError(`결제창 호출 실패: ${(e as Error).message}`);
          }
        } else {
          setError('결제 모듈을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.');
        }
        setIsProcessing(false);
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : '결제 처리 중 오류가 발생했습니다.');
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setSelectedPkg(null);
    setStep('list');
    setError(null);
    setAgreed(false);
    setInicisForm(null);
  };

  return (
    <Layout title="크레딧 충전">
      <div className={styles.page}>
        {/* 잔여 크레딧 표시 (한 줄) */}
        <div className={styles.balanceLine}>
          잔여 메시지 크레딧{' '}
          <span className={`${styles.balanceValue} ${balance !== null && balance < 100 ? styles.balanceWarning : ''}`}>
            {balance !== null ? balance.toLocaleString() : '-'}
          </span>
          <span className={styles.balanceUnit}>건</span>
        </div>

        {/* 포인트 사용처 카탈로그 (KG이니시스 필수 — 포인트로 결제 가능한 상품 등록) */}
        {step === 'list' && (
          <div className={styles.usageCatalog}>
            <div className={styles.catalogTitle}>충전한 포인트로 사용 가능한 서비스</div>
            <div className={styles.catalogGrid}>
              <div className={styles.catalogItem}>
                <div className={styles.catalogIcon}>알림톡</div>
                <div className={styles.catalogBody}>
                  <div className={styles.catalogName}>카카오톡 알림톡 발송</div>
                  <div className={styles.catalogDesc}>
                    매장 키오스크에서 대기 등록한 손님에게 입장 호출 메시지 발송
                  </div>
                </div>
                <div className={styles.catalogPrice}>1포인트 / 건</div>
              </div>
              <div className={styles.catalogItem}>
                <div className={styles.catalogIcon}>SMS</div>
                <div className={styles.catalogBody}>
                  <div className={styles.catalogName}>SMS 문자 발송</div>
                  <div className={styles.catalogDesc}>
                    카카오톡 수신이 거부된 손님에게 자동 대체 발송
                  </div>
                </div>
                <div className={styles.catalogPrice}>1포인트 / 건</div>
              </div>
            </div>
            <div className={styles.catalogNote}>
              사용 트리거: 어드민 페이지 [대기관리] 화면에서 손님 카드의 [호출] 버튼 클릭 시 자동 발송. 발송 1건당 1포인트 차감.
            </div>
          </div>
        )}

        {/* KG이니시스 필수 고지 4가지 */}
        {step === 'list' && (
          <div className={styles.requiredNotice}>
            <ul>
              <li><strong>유효기간:</strong> 충전한 포인트는 충전일로부터 1년(365일) 내 사용 가능, 경과 시 미사용분은 자동 소멸됩니다.</li>
              <li><strong>청약철회:</strong> 충전 후 7일 이내, 미사용분에 한해 청약철회 및 환불이 가능합니다.</li>
              <li><strong>환불 방식:</strong> 환불은 결제하신 카드(또는 카카오페이) 등 원래 결제수단으로만 환급되며, 현금 환급은 불가합니다.</li>
              <li><strong>사용처:</strong> 충전 포인트는 위 [사용 가능한 서비스]에 표기된 알림톡/SMS 발송에만 사용되며, 다른 용도로는 사용할 수 없습니다.</li>
            </ul>
          </div>
        )}

        {error && <div className={styles.errorMsg}>{error}</div>}

        {/* 카드사 심사 진행 중 안내 (카드 비활성화 상태일 때만) */}
        {step === 'list' && !paymentMethods.card && (
          <div className={styles.cardPendingNotice}>
            <strong>현재 신용카드 결제는 카드사 심사 진행 중으로 일시 비활성화되어 있습니다.</strong>
            <br />
            카카오페이로 결제하실 수 있으며, 카드 결제는 카드사 심사 완료 즉시 자동 활성화됩니다.
          </div>
        )}

        {/* 일부 카드사만 심사 중인 경우 (카드 결제는 가능하지만 거절될 수 있는 카드사 안내) */}
        {step === 'list' && paymentMethods.card && paymentMethods.pendingCards.length > 0 && (
          <div className={styles.partialPendingNotice}>
            <strong>일부 카드사가 인증강화 심사 진행 중입니다.</strong>
            <br />
            <span>심사 진행 중: </span>
            {paymentMethods.pendingCards.map((name, idx) => (
              <span key={name}>
                <b>{name}카드</b>
                {idx < paymentMethods.pendingCards.length - 1 && ', '}
              </span>
            ))}
            <br />
            해당 카드사 결제는 거절될 수 있으니 다른 카드 또는 카카오페이를 이용해주세요. 심사 완료 시 자동 정상화됩니다.
          </div>
        )}

        {/* 패키지 선택 */}
        {step === 'list' && (
          <>
            <div className={styles.sectionTitle}>충전 패키지 선택</div>
            <div className={styles.packageGrid}>
              {packages.map((pkg) => {
                // 카드 비활성화 시에는 카드사 한도 안내가 무의미하므로 숨김
                const limit = paymentMethods.card ? getCardLimitWarning(pkg.price) : null;
                return (
                  <button
                    key={pkg.name}
                    className={styles.packageCard}
                    onClick={() => handleSelect(pkg)}
                  >
                    <div className={styles.pkgCredits}>{pkg.credits.toLocaleString()}건</div>
                    <div className={styles.pkgPrice}>{pkg.price.toLocaleString()}원</div>
                    <div className={styles.pkgPerUnit}>건당 {pkg.perUnit}원</div>
                    {limit && (
                      <div
                        className={styles.pkgLimitBadge}
                        style={{
                          background: limit.severity === 'danger' ? '#fef2f2' : '#fffbeb',
                          color: limit.severity === 'danger' ? '#991b1b' : '#78350f',
                          borderColor: limit.severity === 'danger' ? '#fecaca' : '#fde68a',
                        }}
                      >
                        {limit.label}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {paymentMethods.card && (
              <div className={styles.cardLimitNote}>
                <strong>📌 카드사별 결제 한도 안내 (포인트충전 정책)</strong>
                <ul>
                  <li><b>~5만원</b> (1K): 모든 카드 OK</li>
                  <li><b>~10만원</b> (3K): 신한 1일 5만원 한도 → 2회 분할 또는 다른 카드</li>
                  <li><b>~20만원</b> (10K): 신한·현대·NH 불가</li>
                  <li><b>50만원 초과</b> (30K, 50K): <b>비씨카드</b> 또는 <b>하나카드(공인인증 추가)</b>만 가능</li>
                </ul>
                <a href="https://xn--lz2bw7k.com/payment-guide.html#section3-3" target="_blank" rel="noopener noreferrer">
                  전체 카드사 한도표 보기 →
                </a>
              </div>
            )}
          </>
        )}

        {/* 결제 확인 */}
        {step === 'confirm' && selectedPkg && (
          <div className={styles.confirmCard}>
            <div className={styles.confirmTitle}>결제 확인</div>
            <div className={styles.confirmDetail}>
              <span>{selectedPkg.credits.toLocaleString()}건 충전</span>
              <span className={styles.confirmPrice}>{selectedPkg.price.toLocaleString()}원</span>
            </div>
            <label className={styles.agreeRow}>
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                disabled={isProcessing}
              />
              <span>
                <strong>[필수]</strong> 본 상품은 무형 디지털 콘텐츠로서 결제 즉시 제공되며,
                서비스 사용(알림톡 발송 등) 후에는 청약철회가 제한됨에 동의합니다.
                <a href="/terms.html" target="_blank" rel="noopener noreferrer" className={styles.agreeLink}>
                  이용약관 제9조 보기
                </a>
              </span>
            </label>
            <div className={styles.payMethodNote}>
              결제 수단:{' '}
              {paymentMethods.card ? '신용카드 · 카카오페이' : '카카오페이'}{' '}
              (KG이니시스)
              {!paymentMethods.card && (
                <div style={{ marginTop: '4px', fontSize: '0.688rem', color: '#94a3b8' }}>
                  카드 결제는 카드사 심사 완료 후 활성화됩니다.
                </div>
              )}
            </div>
            <div className={styles.confirmButtons}>
              <button className={styles.cancelBtn} onClick={handleReset} disabled={isProcessing}>
                취소
              </button>
              <button className={styles.payBtn} onClick={handleConfirm} disabled={isProcessing || !agreed}>
                {isProcessing ? '결제창 여는 중...' : '결제하기'}
              </button>
            </div>

            {/* KG이니시스 INIStdPay 표준결제 폼 (히든) */}
            {inicisForm && (
              <form
                id={INICIS_FORM_ID}
                method="post"
                style={{ display: 'none' }}
                acceptCharset="UTF-8"
              >
                <input type="hidden" name="version" value={inicisForm.version} />
                <input type="hidden" name="mid" value={inicisForm.mid} />
                <input type="hidden" name="oid" value={inicisForm.oid} />
                <input type="hidden" name="price" value={inicisForm.price} />
                <input type="hidden" name="timestamp" value={inicisForm.timestamp} />
                <input type="hidden" name="use_chkfake" value={inicisForm.use_chkfake} />
                <input type="hidden" name="signature" value={inicisForm.signature} />
                <input type="hidden" name="mKey" value={inicisForm.mKey} />
                <input type="hidden" name="verification" value={inicisForm.verification} />
                <input type="hidden" name="goodname" value={inicisForm.goodname} />
                <input type="hidden" name="buyername" value={inicisForm.buyername} />
                <input type="hidden" name="buyertel" value={inicisForm.buyertel} />
                <input type="hidden" name="buyeremail" value={inicisForm.buyeremail} />
                <input type="hidden" name="returnUrl" value={inicisForm.returnUrl} />
                <input type="hidden" name="closeUrl" value={inicisForm.closeUrl} />
                <input type="hidden" name="gopaymethod" value={inicisForm.gopaymethod} />
                <input type="hidden" name="acceptmethod" value={inicisForm.acceptmethod} />
                <input type="hidden" name="currency" value={inicisForm.currency} />
              </form>
            )}
          </div>
        )}

        {/* 완료 */}
        {step === 'complete' && (
          <div className={styles.completeCard}>
            <div className={styles.completeTitle}>충전 완료</div>
            <div className={styles.completeBalance}>
              잔여 크레딧: <strong>{balance?.toLocaleString()}건</strong>
            </div>
            <button className={styles.doneBtn} onClick={handleReset}>
              확인
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}
